/**
 * Teklif özeti (sağ sütun, yalnız bu kart yapışkan; şartname 4): işaretli seçenekler, aralık, uyarılar ve çıktı eylemleri.
 * Çıktı düğmeleri `aria-disabled` + olay işleyicisinde engel; kapalıyken neden görünür metindir ve düğmeye bağlıdır.
 */
import { HIZLI_METIN as M } from '../metin/hizliTeklif';
import { Ikon } from './Ikon';
import { paraYaz, tutarYaz, type KapiNedeni } from './mantik';

export interface OzetSatiri {
  readonly girdi: string;
  readonly ad: string;
  readonly kombinasyon: string;
  readonly konaklamaMinor: number;
  readonly paketMinor: number;
  /** Serbest kalemler: tüm seçeneklere eklenenler + yalnız bu seçeneğe eklenenler (28.10.2026). */
  readonly ekMinor?: number;
  /** null = paket fiyatı eksik, toplam tamamlanmadı. */
  readonly toplamMinor: number | null;
  readonly para: string;
}

/** Çoklu bölüm planı: her bölümde seçilen tek seçenek, planın paketleri ve genel toplam. */
export interface PlanGorunumu {
  readonly satirlar: readonly { readonly anahtar: string; readonly no: number; readonly ad: string | null; readonly gece: number | null; readonly tarih: string; readonly toplamMinor: number | null; readonly para: string }[];
  readonly paketler: readonly { readonly ad: string; readonly tutarMinor: number | null }[];
  /** Serbest kalemlerin planın tamamı için toplamı (bölüm seçimlerinin özel kalemleri dahil). */
  readonly ekMinor?: number;
  readonly genelToplamMinor: number | null;
  readonly para: string | null;
  readonly paraKarisik: boolean;
}

export interface OzetUyari {
  readonly ton: 'bilgi' | 'uyari' | 'tehlike';
  readonly metin: string;
}

export function TeklifOzeti({
  satirlar,
  aralik,
  odaMetni,
  para,
  uyarilar,
  bilgi,
  kapiAcik,
  neden,
  nedenMetni,
  mesgul,
  pdfOlustur,
  pdfDili,
  pdfDiliDegisti,
  mesajOlustur,
  onayBekliyor,
  onayla,
  vazgec,
  baslik,
  plan,
  sayiMetni,
}: {
  satirlar: readonly OzetSatiri[];
  aralik: { readonly en: number; readonly ust: number } | null;
  odaMetni: string;
  para: string | null;
  uyarilar: readonly OzetUyari[];
  bilgi: string | null;
  kapiAcik: boolean;
  neden: KapiNedeni | null;
  nedenMetni: string | null;
  mesgul: 'pdf' | 'mesaj' | null;
  pdfOlustur: () => void;
  pdfDili: 'tr' | 'en';
  pdfDiliDegisti: (dil: 'tr' | 'en') => void;
  mesajOlustur: () => void;
  onayBekliyor: boolean;
  onayla: () => void;
  vazgec: () => void;
  /** "<Müşteri adı> - Özel <Destinasyon> Tatil Paketi" önizlemesi. */
  baslik: string | null;
  /** İki ve üstü bölüm: plan görünümü (satır listesi yerine). */
  plan: PlanGorunumu | null;
  sayiMetni?: string;
}) {
  const eksik = satirlar.some((s) => s.toplamMinor === null);
  return (
    <aside className="ht-ozet ht-kart" aria-labelledby="ht-ozet-baslik" data-testid="ht-ozet">
      <div className="ht-kart-ust">
        <h2 id="ht-ozet-baslik">{M.ozetBaslik}</h2>
        <span className="ht-sayi-et" data-testid="ht-ozet-sayi">{sayiMetni ?? M.ozetSecenek(satirlar.length)}</span>
      </div>
      {baslik !== null ? <p className="ht-belge-baslik" data-testid="ht-belge-baslik"><span className="ht-ipucu">{M.baslikOnizleme}</span><b>{baslik}</b></p> : null}
      {plan !== null ? (
        <div className="ht-plan" data-testid="ht-plan">
          <span className="ht-plan-baslik">{M.bolum.planBaslik}</span>
          {plan.satirlar.map((p) => (
            <div className="ht-plan-satir" key={p.anahtar} data-testid={`ht-plan-${p.no}`}>
              <b>{p.ad === null ? M.bolum.bolumSecilmedi(p.no) : M.bolum.planSatir(p.no, p.ad, p.gece ?? 0)}</b>
              <span className="ht-fiyat">{p.toplamMinor === null ? '' : paraYaz(p.toplamMinor, p.para)}</span>
              <small>{p.tarih}</small>
            </div>
          ))}
          {plan.paketler.length > 0 ? <span className="ht-plan-baslik">{M.bolum.planPaket}</span> : null}
          {plan.paketler.map((p) => (
            <div className="ht-plan-satir" key={p.ad}><b>{p.ad}</b><span className="ht-fiyat">{p.tutarMinor === null ? M.paketFiyatYok : plan.para === null ? '' : paraYaz(p.tutarMinor, plan.para)}</span></div>
          ))}
          {(plan.ekMinor ?? 0) > 0 && plan.para !== null ? <div className="ht-plan-satir" data-testid="ht-plan-ek"><b>{M.kalem.baslik}</b><span className="ht-fiyat">{paraYaz(plan.ekMinor ?? 0, plan.para)}</span></div> : null}
          <div className="ht-aralik">
            <span>{M.bolum.genelToplam}</span>
            <strong data-testid="ht-genel-toplam">{plan.genelToplamMinor !== null && plan.para !== null ? paraYaz(plan.genelToplamMinor, plan.para) : plan.paraKarisik ? M.bolum.paraFarkli : M.ozetTamamlanmadi}</strong>
          </div>
          <p className="ht-ipucu">{M.bolum.genelKapsam}</p>
        </div>
      ) : satirlar.length === 0 ? <p className="ht-ipucu">{M.ozetBos}</p> : (
        <ul className="ht-ozet-liste">
          {satirlar.map((s) => (
            <li key={s.girdi}>
              <div className="ht-ozet-satir"><b>{s.ad}</b><span className="ht-fiyat" data-testid={`ht-ozet-toplam-${s.girdi}`}>{s.toplamMinor === null ? M.ozetTamamlanmadi : paraYaz(s.toplamMinor, s.para)}</span></div>
              <small>{s.kombinasyon}. {M.ozetKonaklama} {tutarYaz(s.konaklamaMinor)}{s.paketMinor > 0 ? ` + ${M.ozetPaket} ${tutarYaz(s.paketMinor)}` : ''}{(s.ekMinor ?? 0) > 0 ? ` + ${M.kalem.baslik.toLocaleLowerCase('tr-TR')} ${tutarYaz(s.ekMinor ?? 0)}` : ''}</small>
            </li>
          ))}
        </ul>
      )}
      {plan === null && aralik !== null && para !== null && !eksik ? (
        <div className="ht-aralik">
          <span>{M.ozetAralik}</span>
          <strong data-testid="ht-ozet-aralik">{aralik.en === aralik.ust ? paraYaz(aralik.en, para) : `${tutarYaz(aralik.en)} - ${paraYaz(aralik.ust, para)}`}</strong>
        </div>
      ) : null}
      {plan === null && satirlar.length > 0 ? <p className="ht-ipucu">{M.ozetKapsam(odaMetni, para ?? '')}</p> : null}
      {uyarilar.map((u, i) => (
        <div className={`ht-bilgi-kutu ht-bilgi-${u.ton}`} key={i}>
          <Ikon ad={u.ton === 'bilgi' ? 'bilgi' : 'uyari'} />
          <span>{u.metin}</span>
        </div>
      ))}
      {bilgi !== null ? <div className="ht-bilgi-kutu ht-bilgi-bilgi"><Ikon ad="bilgi" /><span>{bilgi}</span></div> : null}
      <label className="ht-alan"><span className="ht-etiket">PDF ve mesaj dili</span>
        <select className="ht-girdi" value={pdfDili} disabled={mesgul !== null} data-testid="ht-pdf-dil" onChange={e => pdfDiliDegisti(e.target.value === 'en' ? 'en' : 'tr')}>
          <option value="tr">Türkçe</option><option value="en">English</option>
        </select>
      </label>
      {pdfDili === 'en' ? <p className="ht-ipucu">Ürünlere yazdığınız özel adlar ve notlar yazıldığı dilde korunur.</p> : null}
      <div className="ht-ozet-eylem">
        <button
          type="button"
          className="ht-btn ht-btn-p"
          aria-disabled={!kapiAcik || mesgul !== null}
          aria-describedby={!kapiAcik ? 'ht-kapi-neden' : undefined}
          data-testid="ht-pdf"
          onClick={() => { if (kapiAcik && mesgul === null) pdfOlustur(); }}
        ><Ikon ad="belge" />{mesgul === 'pdf' ? M.yukleniyor : M.pdfOlustur}</button>
        <button
          type="button"
          className="ht-btn"
          aria-disabled={!kapiAcik || mesgul !== null}
          aria-describedby={!kapiAcik ? 'ht-kapi-neden' : undefined}
          data-testid="ht-mesaj"
          onClick={() => { if (kapiAcik && mesgul === null) mesajOlustur(); }}
        ><Ikon ad="mesaj" />{mesgul === 'mesaj' ? M.yukleniyor : M.mesajOlustur}</button>
      </div>
      {!kapiAcik && nedenMetni !== null ? <p className="ht-kapali" id="ht-kapi-neden" data-testid="ht-kapi-neden" data-neden={neden ?? undefined}><b>{M.kapali}</b> {nedenMetni}</p> : null}
      {onayBekliyor ? (
        <div className="ht-onay" data-testid="ht-kritik-onay">
          <p>{M.kritikOnay}</p>
          <div className="ht-onay-eylem">
            <button type="button" className="ht-btn ht-btn-p" onClick={onayla} data-testid="ht-kritik-evet">{M.kritikOnayEvet}</button>
            <button type="button" className="ht-btn" onClick={vazgec}>{M.kritikOnayHayir}</button>
          </div>
        </div>
      ) : null}
    </aside>
  );
}
